import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Bell, BellOff, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ChatBox from '../components/ChatBox';
import ReviewModal from '../components/ReviewModal';
import ShareTripButton from '../components/ShareTripButton';
import SOSButton from '../components/SOSButton';
import { toast } from '../hooks/use-toast';
import {
  useRideTracking,
  TrackingMap,
  RideStatusBanner,
  DriverInfoCard,
  TripDetailsCard,
  NoShowReportSection
} from './tracking';

const RideTracking = () => {
  const navigate = useNavigate();
  const [showChat, setShowChat] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  const {
    ride,
    driverLocation,
    status,
    eta,
    statusMessage,
    pickupCoords,
    dropoffCoords,
    showReviewModal,
    setShowReviewModal,
    notificationsEnabled,
  } = useRideTracking();

  const handleEnableNotifications = async () => {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      // Can't do anything if user denied
    }
    // State will update via the hook's permission check on next render
    window.location.reload();
  };

  const handleCancelRide = async () => {
    setCancelling(true);
    try {
      const api = (await import('../utils/api')).default;
      await api.put(`/rides/${ride.id}/cancel`, { reason: cancelReason || 'Cancelled by rider' });
      toast({ title: 'Ride Cancelled', description: 'Your ride has been cancelled' });
      setShowCancelDialog(false);
      navigate('/');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel ride', variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = status && ['pending', 'accepted'].includes(status);

  if (!ride) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Connecting to ride tracking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Map Section */}
      <TrackingMap
        pickupCoords={pickupCoords}
        dropoffCoords={dropoffCoords}
        driverLocation={driverLocation}
        status={status}
        eta={eta}
      />

      {/* Ride Details Section */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Live Status Banner */}
        <RideStatusBanner status={status} statusMessage={statusMessage} />

        {/* Driver Info Card */}
        <DriverInfoCard 
          ride={ride} 
          status={status} 
          onShowChat={() => setShowChat(true)} 
        />

        {/* Trip Details Card */}
        <TripDetailsCard pickup={ride.pickup} dropoff={ride.dropoff} />

        {/* Cancel Ride Button */}
        {canCancel && (
          <Button
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => setShowCancelDialog(true)}
            data-testid="rider-cancel-ride-btn"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Cancel Ride
          </Button>
        )}

        {/* Share Trip with Family */}
        {status && !['completed', 'cancelled'].includes(status) && (
          <ShareTripButton rideId={ride.id} />
        )}

        {/* Emergency SOS — visible during active rides */}
        {status && !['completed', 'cancelled'].includes(status) && (
          <SOSButton rideId={ride.id} />
        )}

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-md" data-testid="rider-cancel-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Cancel Ride
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600 mb-3">Are you sure you want to cancel this ride?</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="rider-cancel-reason"
              >
                <option value="">Select a reason</option>
                <option value="changed_mind">Changed my mind</option>
                <option value="driver_taking_too_long">Driver taking too long</option>
                <option value="wrong_location">Wrong pickup/dropoff</option>
                <option value="found_other_transport">Found other transport</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setShowCancelDialog(false)} disabled={cancelling}>
                Go Back
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleCancelRide} disabled={cancelling} data-testid="rider-confirm-cancel-btn">
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Notification indicator */}
        {!notificationsEnabled && Notification.permission !== 'denied' && status !== 'completed' && status !== 'cancelled' && (
          <button
            onClick={handleEnableNotifications}
            className="w-full flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 hover:bg-amber-100 transition-colors"
            data-testid="enable-notifications-btn"
          >
            <BellOff className="h-4 w-4" />
            <span className="text-sm font-medium">Enable notifications to get alerted when your driver is near</span>
          </button>
        )}
        {notificationsEnabled && status !== 'completed' && status !== 'cancelled' && (
          <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="notifications-enabled-badge">
            <Bell className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Notifications enabled — we'll alert you when your driver is near</span>
          </div>
        )}

        {/* Status Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-center font-medium">{statusMessage}</p>
        </div>

        {/* No-Show Report Button */}
        <NoShowReportSection ride={ride} status={status} />

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/rides')}
          >
            View All Rides
          </Button>
          {status === 'completed' && (
            <Button
              className="flex-1 bg-pink-600 hover:bg-pink-700"
              onClick={() => navigate('/')}
            >
              Book Another Ride
            </Button>
          )}
        </div>
      </div>

      {/* Chat Box */}
      {ride && ride.driverId && (
        <ChatBox
          rideId={ride.id}
          currentUserId={JSON.parse(localStorage.getItem('tekstar_user') || '{}').id}
          otherUserName="Driver"
          otherUserRole="Your Driver"
        />
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          navigate('/rides');
        }}
        ride={ride}
        userRole="rider"
        onSubmitSuccess={() => {
          setShowReviewModal(false);
          navigate('/rides');
        }}
      />
    </div>
  );
};

export default RideTracking;
